/// 角度单位 degree(deg) radian(rad) turns(turn) gradian(grad)
/// SVG 默认 deg

/// 判断 CSS 中 transform rotate 旋转方向：
/// 轻握左手，大拇指指向旋转轴正方向，四指指向的方向就是旋转正方向
/// x轴正方向向右
/// y轴正方向向下
/// z轴正方向向外

// css transform 影响的是视觉渲染，而不是布局

// transform: none;
// transform-style: flat; // preserve-3d | flat

// transform-origin: center;
// transform-origin: 50% 50%;
// HTML element 默认坐标系原点在自身的（50%， 50%）处
// SVG element 默认坐标系原点在svg画布的原点（0，0）处
// 实测 SVG element 的 transform-origin 只能设置为百分比，且是相对svg画布的百分比，不是相对 html element 的百分比

// viewBox="0 0 width height"时，svg画布的原点（0，0）在屏幕左上点
// viewBox="x y width height"时，svg画布的原点（0，0）在屏幕左上点（0-x，0-y）处

// transform  4 种变形方式
// # 位移
// 	CSS : translate(tx[, ty=0]) translateX(tx)  translateY(ty)
// 	SVG : translate(tx[ ty=0])
//  连续的 translate(tx1 ty1) translate(tx2 ty2) = translate(tx1+tx2 ty1+ty2)
//  还原 translate(tx ty) = translate(-tx -ty)
// # 旋转
// 	CSS : rotate(angle)
// 	SVG : rotate(angle [centerX centerY])
//  连续的 rotate(a1) rotate(a2) = rotate(a1+a2)
//  还原 rotate(a1) = rotate(-a1)
// # 缩放
// 	CSS : scale(sx[, sy = sx])  scaleX(sx) scaleY(sy)
// 	SVG : scale(sx[ sy = sx])
//  连续的 scale(sx1 sy1) scale(sx2 sy2) = scale(sx1*sx2 sy1*sy2)
//  还原 scale(sx sy) = scale(1/sx 1/sy)
// # 倾斜
// 	CSS : skewX(a) skewY(a)
// 	SVG : skewX(a) skewY(a)
// 	skewX(angle) 所有点x坐标变换，y坐标不变，相当于y轴倾斜，任何与x轴平行的边长度保持不变，y轴正方向向x轴正方向靠近为+angle
// 	skewY(angle) 所有点y坐标变换，x坐标不变，相当于x轴倾斜，任何与y轴平行的边长度保持不变，x轴正方向向y轴正方向靠近为+angle
// 	连续的 skewX(a1) skewX(a2) != skewX(a1 + a2)

// 使用多个变换函数，每一个变换函数不仅改变了元素，同时也会改变和元素关联的transform坐标系
// 当变换函数依次执行时，后一个变换函数总是基于前一个变换后的新transform坐标系
// 即变换函数是通过数学上的矩阵乘法运算完成的，而矩阵的乘法是不满足交换律的
// transform: scale(0.8, 1.5) rotate(35deg) skew(5deg, 20deg) translate(15px, 25px);

/**
 * CSS 坐标系
 * 0deg = 从下到上
 * 90deg = 从左到右
 *                   [0deg]            [45deg]
 *                      ⬆            ⬈
 *                      *         *
 *                      *     *
 *                      *  *
 *  [-90deg] ⬅ *  *  *  *  *  *  * ⮕  [90deg]
 *                      *
 *                      *
 *                      *
 *                      ⬇
 *                   [180deg]
 */

/**
 * canvas svg Vector 坐标系
 *                [-90° / 270°]
 *                  *
 *                  *
 *                  *
 *  [180°] *  *  *  *  *  *  *  [0°]
 *                  *
 *                  *
 *                  *
 *                [90°]
 */

export function toRadian(degree: number) {
  return (degree * Math.PI) / 180;
}

export function toDegree(radian: number) {
  return (radian * 180) / Math.PI;
}

export function length(x: number, y: number, z = 0) {
  return Math.sqrt(x * x + y * y + z * z);
}

export function distance(x1: number, y1: number, x2: number, y2: number) {
  return length(x2 - x1, y2 - y1);
}

// 返回从原点(0,0) 到 (x,y) 点的线段与 x 轴正方向之间的平面角度(弧度值)
export function angle(
  startX: number,
  startY: number,
  endX: number,
  endY: number
): number {
  let radian = Math.atan2(endY - startY, endX - startX);
  if (radian < 0) {
    radian += Math.PI * 2;
  }
  return radian;
}

export function polar(radian: number, length: number) {
  return {
    dx: Math.cos(radian) * length,
    dy: Math.sin(radian) * length
  };
}

export function normalizePoint(x: number, y: number, z?: number) {
  if (typeof z === "number") {
    const l = length(x, y, z);
    return {
      x: (x /= l),
      y: (y /= l),
      z: (z /= l)
    };
  } else {
    const l = length(x, y);
    return {
      x: (x /= l),
      y: (y /= l)
    };
  }
}

// 根据起点终点返回方向
export function getDirection(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  minDistance = 10
): "up" | "down" | "left" | "right" | "no" {
  // 如果滑动距离太短
  if (
    Math.abs(endX - startX) < minDistance &&
    Math.abs(endY - startY) < minDistance
  ) {
    return "no";
  }
  const degree = toDegree(angle(startX, startY, endX, endY));
  if (degree >= -135 && degree <= -45) {
    return "up";
  } else if (degree > 45 && degree < 135) {
    return "down";
  } else if (
    (degree >= 135 && degree <= 180) ||
    (degree >= -180 && degree < -135)
  ) {
    return "left";
  } else {
    // if (degree >= -45 && degree <= 45) {
    return "right";
  }
}
